<template>
  <v-dialog
    v-model="dialog"
    max-width="720"
    content-class="dialog-border-radius"
  >
    <areas-form-dialog-shell
      :title="isEdit ? 'Редактирование участка МТО' : 'Новый участок МТО'"
      :subtitle="isEdit ? '' : 'Укажите название участка и район обслуживания'"
      icon="mdi-map-marker-radius"
      :saving="saving"
      :is-create="!isEdit"
      :save-label="isEdit ? 'Сохранить' : 'Создать участок'"
      @close="close"
      @save="save"
    >
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card outlined class="areas-form-section mb-4">
          <div class="pa-4">
            <div class="areas-form-section__head">
              <div class="areas-form-section__badge">
                <v-icon>mdi-text-box-outline</v-icon>
              </div>
              <div class="areas-form-section__title-wrap">
                <h3 class="areas-form-section__title">Название участка</h3>
                <p class="areas-form-section__desc">Краткое обозначение для списков и карточки участка</p>
              </div>
            </div>
            <v-text-field
              v-model="form.name"
              label="Название"
              prepend-inner-icon="mdi-rename-box-outline"
              outlined
              dense
              flat
              class="areas-form-field border"
              placeholder="Например: Участок №3, ул. Ленина"
              :rules="[v => !!v || 'Обязательное поле']"
            />
          </div>
        </v-card>

        <v-card outlined class="areas-form-section mb-4">
          <div class="pa-4">
            <div class="areas-form-section__head">
              <div class="areas-form-section__badge">
                <v-icon>mdi-map-marker-path</v-icon>
              </div>
              <div class="areas-form-section__title-wrap">
                <h3 class="areas-form-section__title">Район обслуживания</h3>
                <p class="areas-form-section__desc">Участок всегда принадлежит одному району. Мастер определяется районом</p>
              </div>
            </div>
            <v-autocomplete
              v-model="form.service_area_id"
              label="Район обслуживания"
              :items="serviceAreas"
              item-text="name"
              item-value="id"
              prepend-inner-icon="mdi-map-marker-multiple"
              outlined
              dense
              flat
              class="areas-form-field border"
              :rules="[v => !!v || 'Обязательное поле']"
            />
            <areas-form-hint
              v-if="selectedServiceAreaHintRows.length"
              class="mt-3"
              title="Район обслуживания"
              :rows="selectedServiceAreaHintRows"
            />
          </div>
        </v-card>
      </v-form>

      <template #footer>
        <div class="areas-form-footer-info">
          <div class="areas-form-footer-info__icon-wrap">
            <v-icon class="areas-form-footer-info__icon">mdi-information-outline</v-icon>
          </div>
          <p class="areas-form-footer-info__text mb-0">
            После создания участка можно привязать домофоны, шлагбаумы и другие устройства
            на странице участка. Дополнительные входы и калитки подтянутся вместе с основным домофоном.
          </p>
        </div>
      </template>
    </areas-form-dialog-shell>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import AreasFormHint from '@/components/areas/AreasFormHint';
import AreasFormDialogShell from '@/components/areas/AreasFormDialogShell';

export default {
  name: 'MtoSectorFormDialog',
  components: { AreasFormHint, AreasFormDialogShell },
  props: {
    value: Boolean,
    item: {
      type: Object,
      default: null,
    },
    serviceAreas: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      valid: false,
      saving: false,
      form: {
        name: '',
        service_area_id: null,
      },
    };
  },
  computed: {
    dialog: {
      get () { return this.value; },
      set (val) { this.$emit('input', val); },
    },
    isEdit () {
      return !!(this.item && this.item.id);
    },
    selectedServiceArea () {
      if (!this.form.service_area_id) return null;
      return this.serviceAreas.find(area => Number(area.id) === Number(this.form.service_area_id)) || null;
    },
    selectedServiceAreaHintRows () {
      if (!this.selectedServiceArea) return [];
      const rows = [];
      const masterName = this.selectedServiceArea.master?.name || this.selectedServiceArea.masters?.[0]?.name;
      if (masterName) {
        rows.push({ label: 'Мастер', value: masterName });
      }
      if (this.selectedServiceArea.mto_sectors_count !== undefined) {
        rows.push({ label: 'Участков МТО', value: String(this.selectedServiceArea.mto_sectors_count) });
      }
      return rows;
    },
  },
  watch: {
    value (open) {
      if (open) {
        this.resetForm();
      }
    },
  },
  methods: {
    ...mapActions({
      createMtoSector: 'MtoSector/createMtoSector',
      updateMtoSector: 'MtoSector/updateMtoSector',
    }),
    resetForm () {
      if (this.isEdit) {
        this.form = {
          name: this.item.name,
          service_area_id: this.item.service_area_id,
        };
      } else {
        this.form = {
          name: '',
          service_area_id: null,
        };
      }
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    close () {
      this.dialog = false;
    },
    save () {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      const payload = { ...this.form };
      const action = this.isEdit
        ? this.updateMtoSector({ id: this.item.id, data: payload })
        : this.createMtoSector(payload);

      action
        .then(() => {
          this.$flash_success('Сохранено');
          this.$emit('saved');
          this.close();
        })
        .catch((err) => {
          this.handleError(err);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    handleError (err) {
      if (err.response && err.response.status === 422) {
        const response = err.response.data;
        for (const input in response) {
          if (Array.isArray(response[input])) {
            response[input].forEach(error => this.$flash_warning(`${input}: ${error}`));
          }
        }
      } else {
        this.$flash_warning('Ошибка при сохранении');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './areasFormDialog.scss';
</style>
